// @ts-strict-ignore
import {apiClient} from 'common/util/api-client';
import {FetchOptions} from 'common/util/shared-client';

import {appApiUrl} from '../utils';
import type {PublicDashboardLink} from './public-dashboard-links-client-types';

/* Public Dashboards Links are app_api/projects/public_dashboards/views.py. Should only be used in authenticated context to
 * get public dashboard links or create public dashboard link */
class PublicDashboardLinksClient {
  public PublicDashboardQueryStringKey = `dashboard_id`;
  public QueryStringArrayFormat = `bracket`;

  buildPublicDashboardEndpoint({workspaceId, projectId}) {
    return appApiUrl(`public-dashboards/`, {projectId, workspaceId});
  }

  async querySingle(
    {dashboardId, workspaceId, projectId}: {dashboardId: string; workspaceId?: string; projectId?: string} = {
      dashboardId: null,
      workspaceId: null,
      projectId: null,
    },
    fetchOptions: FetchOptions = {},
  ): Promise<PublicDashboardLink> {
    const url = this.buildPublicDashboardEndpoint({workspaceId, projectId}) + `${dashboardId}`;
    return apiClient.get(url, fetchOptions);
  }

  // queryAll will prioritize workspaceId before projectId
  // If no workspaceId is present, it will return all public dashboard links in project
  async queryAll(
    {workspaceId, projectId}: {workspaceId?: string; projectId?: string} = {
      workspaceId: null,
      projectId: null,
    },
    fetchOptions: FetchOptions = {},
    query: {[key: string]: any} = null,
  ): Promise<PublicDashboardLink[]> {
    const url = this.buildPublicDashboardEndpoint({workspaceId, projectId});
    return apiClient.get(url, query, fetchOptions, this.QueryStringArrayFormat);
  }

  // Given a dashboard_id, return all public dashboard links
  async getPublicDashboardLinksByDashboardId(
    {dashboardId, workspaceId, projectId}: {dashboardId: string; workspaceId?: string; projectId?: string} = {
      dashboardId: null,
      workspaceId: null,
      projectId: null,
    },
    fetchOptions: FetchOptions = {},
  ): Promise<PublicDashboardLink[]> {
    const query = dashboardId ? {[this.PublicDashboardQueryStringKey]: [dashboardId]} : null;
    const response = await this.queryAll({workspaceId, projectId}, fetchOptions, query);
    return response;
  }

  async deletePublicDashboardLink(
    {dashboardId, workspaceId, projectId}: {dashboardId: string; workspaceId?: string; projectId?: string} = {
      dashboardId: null,
      workspaceId: null,
      projectId: null,
    },
    fetchOptions: FetchOptions = {},
  ) {
    const url = this.buildPublicDashboardEndpoint({workspaceId, projectId}) + `${dashboardId}`;
    return apiClient.delete(url, fetchOptions);
  }

  async createPublicDashboardLink(
    {dashboardId, workspaceId, projectId}: {dashboardId: string; workspaceId?: string; projectId?: string} = {
      dashboardId: null,
      workspaceId: null,
      projectId: null,
    },
  ): Promise<PublicDashboardLink> {
    const url = this.buildPublicDashboardEndpoint({workspaceId, projectId}) + `${dashboardId}`;
    return apiClient.post(url, {});
  }

  buildPublicDashboardPasswordEndpoint({workspaceId, projectId, linkId}) {
    return `${this.buildPublicDashboardEndpoint({workspaceId, projectId})}${linkId}/password`;
  }

  async setPublicDashboardLinkPassword(
    {
      linkId,
      workspaceId,
      projectId,
      password,
    }: {linkId: string; workspaceId?: string; projectId?: string; password: string} = {
      linkId: null,
      workspaceId: null,
      projectId: null,
      password: null,
    },
  ): Promise<EmptyObj> {
    const url = this.buildPublicDashboardPasswordEndpoint({workspaceId, projectId, linkId});
    return apiClient.post(url, {password});
  }

  async deletePublicDashboardLinkPassword(
    {linkId, workspaceId, projectId}: {linkId: string; workspaceId?: string; projectId?: string} = {
      linkId: null,
      workspaceId: null,
      projectId: null,
    },
  ): Promise<EmptyObj> {
    const url = this.buildPublicDashboardPasswordEndpoint({workspaceId, projectId, linkId});
    return apiClient.delete(url, {});
  }
}

export const publicDashboardLinksClient = new PublicDashboardLinksClient();
